/**
 * FirstTimeUserBanner Component
 * Dismissible banner shown to new users to introduce the help system
 * Uses localStorage to remember if user has dismissed it
 */
import React, { useState, useEffect } from 'react'
import { X, BookOpen, Sparkles } from 'lucide-react'
import clsx from 'clsx'

const STORAGE_KEY = 'fast-cortex-help-banner-dismissed'

/**
 * FirstTimeUserBanner Component
 */
export default function FirstTimeUserBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    // Check if user has dismissed the banner before
    const dismissed = localStorage.getItem(STORAGE_KEY)
    if (!dismissed) {
      // Show banner after a short delay for better UX
      setTimeout(() => {
        setIsVisible(true)
        setIsAnimating(true)
      }, 500)
    }
  }, [])

  const handleDismiss = () => {
    setIsAnimating(false)
    setTimeout(() => {
      setIsVisible(false)
      localStorage.setItem(STORAGE_KEY, 'true')
    }, 300) // Wait for animation to complete
  }

  const handleExplore = () => {
    // Open help in new window
    window.open('/help/getting-started/welcome', '_blank', 'noopener,noreferrer')
    handleDismiss()
  }

  if (!isVisible) {
    return null
  }

  return (
    <div
      className={clsx(
        'fixed bottom-4 right-4 max-w-md z-50',
        'transition-all duration-300 ease-out',
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      )}
    >
      <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg shadow-2xl p-5 text-white">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Icon */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 bg-white/20 rounded-full p-2">
            <Sparkles className="h-6 w-6" />
          </div>

          <div className="flex-1">
            {/* Heading */}
            <h3 className="text-lg font-semibold mb-1">
              New to Therion Cortex?
            </h3>

            {/* Description */}
            <p className="text-sm text-blue-50 mb-4">
              Explore our comprehensive help centre to learn about margins, calculations, and how to get the most out of Therion Cortex.
            </p>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleExplore}
                className="flex items-center gap-1.5 px-4 py-2 bg-white text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors"
              >
                <BookOpen className="h-4 w-4" />
                Get Started
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-white text-sm font-medium hover:bg-white/10 rounded-md transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Reset banner visibility (for testing or admin purposes)
 */
export function resetFirstTimeBanner() {
  localStorage.removeItem(STORAGE_KEY)
}
